
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface BlogPost {
  id: string;
  title: string;
  excerpt: string;
  author: string;
  date: string;
  readTime: string;
  category: string;
  image: string;
  featured?: boolean;
}

const blogPosts: BlogPost[] = [
  {
    id: 'landing-page-elements',
    title: '10 Essential Elements Every High-Converting Landing Page Must Have',
    excerpt: 'Discover the key components that turn visitors into customers and boost your conversion rates.',
    author: 'Sarah Johnson',
    date: '2024-01-15',
    readTime: '8 min read',
    category: 'Conversion Optimization',
    image: 'https://readdy.ai/api/search-image?query=Modern%20website%20landing%20page%20design%20on%20computer%20screen%2C%20clean%20user%20interface%2C%20conversion%20optimization%20elements%2C%20professional%20web%20design%20showcase&width=400&height=250&seq=blog-1&orientation=landscape'
  },
  {
    category: 'Strategy',
    title: '10 Essential Elements Every High-Converting Landing Page Must Have',
    excerpt: 'Learn the proven components that turn visitors into customers and how to implement them effectively in your funnels.',
    author: 'Michael Chen',
    date: 'March 10, 2024',
    readTime: '8 min read',
    image: 'https://readdy.ai/api/search-image?query=Modern%20website%20landing%20page%20design%20on%20computer%20screen%2C%20clean%20user%20interface%2C%20conversion%20optimization%20elements%2C%20professional%20web%20design%20showcase&width=400&height=250&seq=blog-1&orientation=landscape'
  },
  {
    category: 'Optimization',
    title: 'A/B Testing Your Funnels: What to Test and When',
    excerpt: 'Master the art of split testing with our comprehensive guide to funnel optimization and data-driven decision making.',
    author: 'Emily Rodriguez',
    date: 'March 8, 2024',
    readTime: '6 min read',
    image: 'https://readdy.ai/api/search-image?query=A%2FB%20testing%20analytics%20dashboard%20with%20split%20test%20results%2C%20data%20comparison%20charts%2C%20conversion%20rate%20optimization%20tools%2C%20testing%20interface&width=400&height=250&seq=blog-2&orientation=landscape'
  },
  {
    category: 'Tools',
    title: 'ClickFunnels vs GoHighLevel: Which Platform is Right for You?',
    excerpt: 'An in-depth comparison of the two leading funnel builders to help you choose the perfect platform for your business.',
    author: 'David Thompson',
    date: 'March 5, 2024',
    readTime: '12 min read',
    image: 'https://readdy.ai/api/search-image?query=Software%20comparison%20interface%20showing%20different%20marketing%20platforms%2C%20tool%20selection%20dashboard%2C%20business%20software%20evaluation%2C%20platform%20comparison&width=400&height=250&seq=blog-3&orientation=landscape'
  },
  {
    category: 'Case Study',
    title: 'From Zero to $100K: SaaS Funnel Success Story',
    excerpt: 'How we helped a startup SaaS company build their first sales funnel and achieve six-figure revenue in 6 months.',
    author: 'Sarah Johnson',
    date: 'March 1, 2024',
    readTime: '10 min read',
    image: 'https://readdy.ai/api/search-image?query=SaaS%20business%20growth%20chart%20showing%20revenue%20increase%2C%20startup%20success%20visualization%2C%20business%20analytics%20dashboard%2C%20growth%20metrics%20display&width=400&height=250&seq=blog-4&orientation=landscape'
  },
  {
    category: 'Email Marketing',
    title: 'Crafting Email Sequences That Convert: A Step-by-Step Guide',
    excerpt: 'Build email campaigns that nurture leads and drive sales with our proven framework for high-converting sequences.',
    author: 'Michael Chen',
    date: 'February 25, 2024',
    readTime: '7 min read',
    image: 'https://readdy.ai/api/search-image?query=Email%20marketing%20campaign%20interface%2C%20email%20sequence%20automation%20workflow%2C%20marketing%20automation%20dashboard%2C%20professional%20email%20design&width=400&height=250&seq=blog-5&orientation=landscape'
  },
  {
    category: 'Psychology',
    title: 'The Psychology of Persuasion in Funnel Design',
    excerpt: 'Understand the cognitive triggers that influence buying decisions and how to ethically implement them in your funnels.',
    author: 'Emily Rodriguez',
    date: 'February 20, 2024',
    readTime: '9 min read',
    image: 'https://readdy.ai/api/search-image?query=Consumer%20psychology%20visualization%2C%20buyer%20decision%20making%20process%2C%20persuasion%20principles%20in%20marketing%2C%20behavioral%20psychology%20concepts&width=400&height=250&seq=blog-6&orientation=landscape'
  }
];

export default function BlogGrid() {
  const [selectedCategory, setSelectedCategory] = useState('All');
  const navigate = useNavigate();

  const categories = ['All', 'Conversion Optimization', 'Sales Funnels', 'Marketing Automation', 'E-commerce', 'Analytics'];
  
  const filteredPosts = selectedCategory === 'All' 
    ? blogPosts 
    : blogPosts.filter(post => post.category === selectedCategory);

  const handlePostClick = (postId: string) => {
    navigate(`/blog/${postId}`);
  };

  return (
    <section className="py-16 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Category Filter */}
        <div className="flex flex-wrap justify-center gap-4 mb-12">
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => setSelectedCategory(category)}
              className={`px-6 py-2 rounded-full font-medium transition-all duration-300 cursor-pointer ${
                selectedCategory === category
                  ? 'bg-blue-600 text-white shadow-lg'
                  : 'bg-white text-gray-600 hover:bg-blue-50 hover:text-blue-600'
              }`}
            >
              {category}
            </button>
          ))}
        </div>

        {/* Blog Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredPosts.map((post) => (
            <article 
              key={post.id} 
              className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 group cursor-pointer"
              onClick={() => handlePostClick(post.id)}
            >
              <div className="relative overflow-hidden">
                <img 
                  alt={post.title}
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-300" 
                  src={post.image}
                />
                <div className="absolute top-4 left-4">
                  <span className="px-3 py-1 bg-white/90 backdrop-blur-sm rounded-full text-xs font-medium text-gray-800">
                    {post.category}
                  </span>
                </div>
              </div>
              
              <div className="p-6">
                <div className="flex items-center text-sm text-gray-500 mb-3">
                  <span>{post.author}</span>
                  <span className="mx-2">•</span>
                  <span>{new Date(post.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                  <span className="mx-2">•</span>
                  <span>{post.readTime}</span>
                </div>
                
                <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors line-clamp-2">
                  {post.title}
                </h3>
                
                <p className="text-gray-600 mb-4 line-clamp-3">
                  {post.excerpt}
                </p>
                
                <div className="flex items-center justify-between">
                  <span className="text-blue-600 font-medium group-hover:text-blue-700 transition-colors">
                    Read more →
                  </span>
                  <div className="flex items-center gap-2">
                    <button className="p-2 text-gray-400 hover:text-red-5
00 transition-colors">
                      <i className="ri-heart-line"></i>
                    </button>
                    <button className="p-2 text-gray-400 hover:text-blue-500 transition-colors">
                      <i className="ri-share-line"></i>
                    </button>
                  </div>
                </div>
              </div>
            </article>
          ))}
        </div>

        {/* Load More Button */}
        <div className="text-center mt-12">
          <button className="px-8 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium cursor-pointer">
            Load More Articles
          </button>
        </div>
      </div>
      
      <style jsx>{`
        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
        .line-clamp-3 {
          display: -webkit-box;
          -webkit-line-clamp: 3;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
    </section>
  );
}
